// Tema moderno y futbolero: base verde césped + azul + blanco.
// `accent` = verde (primario), `gold` = azul (secundario/realce). `live` = rojo (solo "en juego").

export interface AppTheme {
  name: 'light' | 'dark';
  colors: {
    bg: string;
    surface: string;
    surfaceAlt: string;
    border: string;
    borderStrong: string;
    text: string;
    textMuted: string;
    accent: string;
    accentDark: string; // extremo oscuro de los degradés (derivado del acento)
    accentText: string; // texto SOBRE el acento (CTA): elegido por luminancia, ≥4.5:1 (ver lib/color.ts)
    accentInk: string;  // acento apto como TEXTO chico sobre surface (≥4.5:1; en claro es más oscuro que accent)
    accentBorder: string; // acento como BORDE/realce sobre surface (≥3:1 no-texto); derivado para acentos custom
    gold: string; // (histórico: nombre legado) → ahora azul secundario
    live: string;
    danger: string;
    timba: string; // verde manzana: realce del pronóstico cargado en la card del partido
    boost: string; // ámbar: badge de booster (×2/×3/×4) de la timba
    boostText: string; // texto sobre fondos `boost` (badge de la nav)
    pick: string;  // celeste: partido con pronóstico mío cargado
    pickText: string; // texto sobre fondos `pick` (franja embocada, TimbaBar): oscuro en dark, claro en light
    hit: string;   // dorado: pronóstico que sumó puntos (embocado)
    miss: string;  // gris: partido terminado sin sumar puntos (franja + contorno)
  };
  radius: string;
  maxWidth: string;
}

export const dark: AppTheme = {
  name: 'dark',
  colors: {
    bg: '#0c1622',
    surface: '#13202f',
    surfaceAlt: '#1b2c3f',
    border: '#26384d',
    borderStrong: '#3a506b',
    text: '#eef3f8',
    textMuted: '#aebdd0',
    accent: '#f97316', // naranja fuerte (color base por defecto)
    accentDark: '#7c2d12',
    accentText: '#0f172a', // tinta oscura: blanco s/#f97316 da 2.80:1 (falla AA); esto da 6.37:1
    accentInk: '#f97316',  // el acento ya pasa como texto sobre surface oscura (5.87:1)
    accentBorder: '#f97316', // el acento default ya pasa como borde (5.87:1 ≥ 3)
    gold: '#4b8df8', // azul (antes #3b82f6: 4.48:1, justo bajo AA; este da 5.07:1)
    live: '#ef4444',
    danger: '#ef4444',
    timba: '#9acd32', // verde manzana
    boost: '#f59e0b', // ámbar
    boostText: '#000000',
    pick: '#38bdf8',  // celeste
    pickText: '#06283a', // 7.14:1 sobre pick
    hit: '#fbbf24',   // dorado
    miss: '#94a3b8',  // gris
  },
  radius: '14px',
  maxWidth: '720px',
};

export const light: AppTheme = {
  name: 'light',
  colors: {
    bg: '#eef3f7',
    surface: '#ffffff',
    surfaceAlt: '#e4edf3',
    border: '#d4dfe9',
    borderStrong: '#aebccd',
    text: '#0e1b2a',
    textMuted: '#475263',
    accent: '#ea580c', // naranja fuerte (color base por defecto)
    accentDark: '#7c2d12',
    accentText: '#0f172a', // tinta oscura: blanco s/#ea580c da 3.56:1 (falla AA texto normal); esto da 5.02:1
    accentInk: '#c2410c',  // naranja más oscuro para texto chico sobre blanco (5.18:1; accent da 3.56)
    accentBorder: '#ea580c', // el acento default ya pasa como borde (3.56:1 ≥ 3)
    gold: '#2563eb', // azul (5.17:1 sobre blanco)
    live: '#dc2626',
    danger: '#dc2626',
    timba: '#9acd32', // verde manzana
    boost: '#b45309', // ámbar oscurecido (antes #d97706: 3.19:1 como texto; este da 5.02:1)
    boostText: '#ffffff',
    pick: '#0369a1',  // celeste oscurecido (antes #0ea5e9: 2.50:1 como texto; este da 5.35:1)
    pickText: '#f0f9ff', // 5.57:1 sobre pick
    hit: '#e0a100',   // dorado
    miss: '#8492a4',  // gris (antes #8a97a8: borde 2.97:1; este da 3.17:1 borde / 4.63:1 texto)
  },
  radius: '14px',
  maxWidth: '720px',
};

export const themes = { dark, light };
